import { useCallback, useEffect, useRef, useState } from "react";

import { logger } from "../core/logging/logger";
import type { PaymentNetwork } from "../domains/transactions/customer-instruction";
import { ServiceLevel } from "../domains/transactions/customer-instruction";

export function delay(time: number) {
  return new Promise<void>((resolve) => setTimeout(resolve, time));
}

export function useAsyncEffect(
  effect: () => Promise<void>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  deps?: readonly any[],
) {
  useEffect(() => {
    const maybePromise = effect();
    if (maybePromise && typeof maybePromise.then === "function") {
      maybePromise.then();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

export function useAsyncFunction<T>(
  fun: (forceRefresh?: boolean) => Promise<T>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  deps: readonly any[] = [],
) {
  const [loading, setLoading] = useState(false);
  const [redoLoading, setRedoLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [result, setResult] = useState<T | null>(null);
  const [tries, setTries] = useState<number>(0);

  useAsyncEffect(async () => {
    try {
      setTries(0);
      setResult(null);
      setError(null);
      setLoading(true);
      const res = await fun();
      setResult(res);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [setError, setLoading, setResult, ...deps]);

  useAsyncEffect(async () => {
    if (tries !== 0) {
      try {
        setResult(null);
        setError(null);
        const res = await fun(true);
        setResult(res);
      } catch (e) {
        setError(e);
      } finally {
        setRedoLoading(false);
      }
    }
  }, [setError, setLoading, setResult, tries]);

  const redo = useCallback(() => {
    setRedoLoading(true);
    setTries((tries) => tries + 1);
  }, []);

  return {
    loading,
    error,
    result,
    redo,
    redoLoading,
  };
}

export function repeat<T>(count: number, factory: (index: number) => T) {
  return new Array(count).fill(null).map((_, index) => factory(index));
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isAnNotEmptyArray(value: any) {
  return Array.isArray(value) && value.length > 0;
}

export function useMounted() {
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  return mounted;
}

type AsyncOperationResult<T> = [() => Promise<T | undefined>, boolean, string | null];

export function useAsyncOperation<T>(operation: () => Promise<T>): AsyncOperationResult<T> {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isCancelled = useRef(false);
  useEffect(
    () => () => {
      isCancelled.current = true;
    },
    [],
  );
  const operationWrapper = useCallback(async () => {
    try {
      setLoading(true);
      const result = await operation();
      if (!isCancelled.current) {
        setError(null);
        setLoading(false);
      }
      return result;
    } catch (err) {
      logger.debug("useAsyncOperation", operation.name, err);
      if (!isCancelled.current) {
        setError(err?.response?.data?.error?.message || err.toString());
      }
    } finally {
      if (!isCancelled.current) {
        setLoading(false);
      }
    }
  }, [operation]);
  return [operationWrapper, loading, error];
}

/**
 * Determines if an object is empty or not
 * @param object A simple plain object
 * @returns true | false
 */
export const isObjectEmpty = (object: object) => Object.keys(object).length === 0;

/**
 * Filters the object properties
 * @param obj A simple plain object
 * @param keys Object keys to exclude or include
 * @param mode Type of filtering action
 * @returns Filtered object
 */
export const filterObjectKeys = (obj: object, keys: string[], mode: "include" | "exclude" = "exclude") =>
  Object.fromEntries(
    Object.entries(obj).filter(([key]) => (mode === "include" ? keys.includes(key) : !keys.includes(key))),
  );

/**
 * Returns the gender type based on integer value
 * @param int Integer either 0 or 1
 * @returns Gender type
 */
export const getGenderByInt = (int = 0 | 1) => {
  switch (int) {
    case 0:
      return "female";
    case 1:
      return "male";
    default:
      return "unknown";
  }
};

/**
 * Returns unique payment networks by service level
 * @param {object[]} paymentNetworks Array of payment networks
 * @returns {object[]} Unique payment networks
 */
export const getUniqueNetworksByServiceLevel = (paymentNetworks) =>
  Array.from(new Map(paymentNetworks.map((network) => [network.serviceLevel, network])).values());

/**
 * Determines if SCT_INST is enabled or not
 * @param networkList Array of payment networks
 * @returns boolean if SCT_INST available in the list of networks
 */
export const isSctEnabled = (networkList: PaymentNetwork[]) => {
  if (!networkList) return false;
  return !!networkList.find((network) => network.serviceLevel === ServiceLevel.SCT_INST);
};
